import { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { space } from '../../../../styles/onboardingUiTokens';
import { FormFieldError } from './formFieldLayout';
import { GroupLabelLegend } from './Label';
import { TextInput } from './TextInput';

const Fieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: none;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${space.sm};
  width: 100%;
`;

const Cell = styled.div<{ $flex: number }>`
  flex: ${({ $flex }) => $flex};
  min-width: 0;
`;

export type BirthdayInputProps = {
  /**
   * `YYYY-MM-DD` as typed, or `''` while a box is still empty. Not validated: 31.02. comes through as
   * `YYYY-02-31`, so the caller decides which dates it accepts and passes the message as `error`.
   */
  value: string;
  onChange: (value: string) => void;
  legend?: string;
  error?: string;
};

type Parts = { day: string; month: string; year: string };

const EMPTY_PARTS: Parts = { day: '', month: '', year: '' };

const splitValue = (value: string): Parts => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? { day: match[3], month: match[2], year: match[1] } : EMPTY_PARTS;
};

const toValue = ({ day, month, year }: Parts): string =>
  day !== '' && month !== '' && year.length === 4 ? `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}` : '';

const digitsOnly = (value: string, maxLength: number) => value.replace(/\D/g, '').slice(0, maxLength);

export function BirthdayInput(props: BirthdayInputProps) {
  const { value, onChange, legend, error } = props;

  const rootId = `${useId()}-birthday`;
  const errorId = `${rootId}-error`;

  const [parts, setParts] = useState<Parts>(() => splitValue(value));
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // Skips the value this component just reported. Emptying a box reports `''`, which would otherwise
  // come straight back as an empty `value` and wipe the digits in the other boxes.
  const lastReported = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (value === lastReported.current) return;
    setParts(splitValue(value));
  }, [value]);

  const update = (next: Parts) => {
    setParts(next);
    const nextValue = toValue(next);
    if (nextValue === lastReported.current) return;
    lastReported.current = nextValue;
    onChange(nextValue);
  };

  const handleDayChange = (input: string) => {
    const day = digitsOnly(input, 2);
    if (day !== '' && Number(day) > 31) return;
    update({ ...parts, day });
    if (day.length === 2) monthRef.current?.focus();
  };

  const handleMonthChange = (input: string) => {
    const month = digitsOnly(input, 2);
    if (month !== '' && Number(month) > 12) return;
    update({ ...parts, month });
    if (month.length === 2) yearRef.current?.focus();
  };

  const handleYearChange = (input: string) => {
    update({ ...parts, year: digitsOnly(input, 4) });
  };

  const hasError = Boolean(error?.trim());

  return (
    <Fieldset id={rootId}>
      {legend ? <GroupLabelLegend>{legend}</GroupLabelLegend> : null}
      <Row>
        <Cell $flex={1}>
          <TextInput
            id={`${rootId}-day`}
            name="birthday-day"
            label="Tag"
            labelVariant="compact"
            value={parts.day}
            onChange={handleDayChange}
            placeholder="TT"
            inputMode="numeric"
            autoComplete="bday-day"
            ariaInvalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
          />
        </Cell>
        <Cell $flex={1}>
          <TextInput
            ref={monthRef}
            id={`${rootId}-month`}
            name="birthday-month"
            label="Monat"
            labelVariant="compact"
            value={parts.month}
            onChange={handleMonthChange}
            placeholder="MM"
            inputMode="numeric"
            autoComplete="bday-month"
            ariaInvalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
          />
        </Cell>
        <Cell $flex={1.5}>
          <TextInput
            ref={yearRef}
            id={`${rootId}-year`}
            name="birthday-year"
            label="Jahr"
            labelVariant="compact"
            value={parts.year}
            onChange={handleYearChange}
            placeholder="JJJJ"
            inputMode="numeric"
            autoComplete="bday-year"
            ariaInvalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
          />
        </Cell>
      </Row>
      <FormFieldError id={errorId} message={error} />
    </Fieldset>
  );
}
